/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { Trash2 } from 'lucide-react';
import { type JSX, useCallback } from 'react';
import { type Control, type FieldPath, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';

import { type FieldDef, INTAKE_SECTION_DEFS } from './intake-config.js';
import {
  CheckboxGridField,
  ChoiceField,
  MultiChoiceField,
  type Option,
  SelectField,
  TextareaField,
  TextField,
} from './intake-fields.js';

import type { IntakeSection, IntakeSubmitInput } from '@therapy/shared-validators';
import type { TFunction } from 'i18next';

type Ctrl = Control<IntakeSubmitInput>;
type Name = FieldPath<IntakeSubmitInput>;

const LABEL_CLASS = 'mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300';
const EMPTY_GUARDIAN = { name: '', relationship: '', contact: '', email: '' } as const;
type GuardianKey = 'name' | 'relationship' | 'contact' | 'email';

/** Typed guardian field path — the numeric index defeats RHF's literal inference. */
function guardianField(index: number, key: GuardianKey): Name {
  return `basicDetails.guardians.${String(index)}.${key}` as Name;
}

function optionsFor(t: TFunction, def: FieldDef): readonly Option[] {
  if (def.options === undefined || def.optPrefix === undefined) {
    return [];
  }
  const prefix = def.optPrefix;
  return def.options.map((v) => ({ value: v, label: t(`intake.opt.${prefix}.${v}`) }));
}

/** One parent/guardian row (extracted so the remove handler is a stable callback). */
function GuardianRow({
  control,
  index,
  canRemove,
  onRemove,
}: {
  readonly control: Ctrl;
  readonly index: number;
  readonly canRemove: boolean;
  readonly onRemove: (index: number) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [onRemove, index]);
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800">
      <TextField
        control={control}
        name={guardianField(index, 'name')}
        label={t('intake.field.guardianName')}
      />
      <TextField
        control={control}
        name={guardianField(index, 'relationship')}
        label={t('intake.field.guardianRelationship')}
      />
      <TextField
        control={control}
        name={guardianField(index, 'contact')}
        label={t('intake.field.guardianContact')}
      />
      <TextField
        control={control}
        name={guardianField(index, 'email')}
        label={t('intake.field.guardianEmail')}
        type="email"
      />
      {canRemove && (
        <button
          type="button"
          className="focus-ring inline-flex items-center gap-1 text-xs font-medium text-red-600"
          onClick={handleRemove}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          {t('intake.removeGuardian')}
        </button>
      )}
    </div>
  );
}

/** Dynamic parent/guardian list (Section 1) backed by RHF `useFieldArray`. */
function GuardiansField({ control }: { readonly control: Ctrl }): JSX.Element {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({ control, name: 'basicDetails.guardians' });
  const onAdd = useCallback(() => {
    append({ ...EMPTY_GUARDIAN });
  }, [append]);
  const onRemove = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove],
  );
  return (
    <div>
      <p className={LABEL_CLASS}>{t('intake.field.guardians')}</p>
      <p className="-mt-0.5 mb-2 text-xs text-slate-400">{t('intake.guardiansHint')}</p>
      <div className="space-y-3">
        {fields.map((gf, i) => (
          <GuardianRow
            key={gf.id}
            control={control}
            index={i}
            canRemove={fields.length > 1}
            onRemove={onRemove}
          />
        ))}
      </div>
      <Button type="button" variant="outline" className="mt-3" onClick={onAdd}>
        {t('intake.addGuardian')}
      </Button>
    </div>
  );
}

function FieldView({
  control,
  def,
}: {
  readonly control: Ctrl;
  readonly def: FieldDef;
}): JSX.Element {
  const { t } = useTranslation();
  const label = t(`intake.field.${def.labelKey}`);
  const options = optionsFor(t, def);
  switch (def.kind) {
    case 'text':
      return <TextField control={control} name={def.name} label={label} />;
    case 'date':
      return <TextField control={control} name={def.name} label={label} type="date" />;
    case 'number':
      return <TextField control={control} name={def.name} label={label} type="number" />;
    case 'textarea':
      return <TextareaField control={control} name={def.name} label={label} />;
    case 'select':
      return (
        <SelectField
          control={control}
          name={def.name}
          label={label}
          options={options}
          placeholder={t('intake.selectPlaceholder')}
        />
      );
    case 'choice':
      return <ChoiceField control={control} name={def.name} label={label} options={options} />;
    case 'multichoice':
      return <MultiChoiceField control={control} name={def.name} label={label} options={options} />;
    case 'grid':
      return (
        <CheckboxGridField control={control} name={def.name} label={label} options={options} />
      );
    case 'guardians':
      return <GuardiansField control={control} />;
  }
}

/** A dependent field — watches its controlling field and renders only on a match. */
function ConditionalField({
  control,
  def,
}: {
  readonly control: Ctrl;
  readonly def: FieldDef;
}): JSX.Element | null {
  const watched = useWatch({ control, name: def.showWhen?.name ?? def.name });
  const allowed = def.showWhen?.in ?? [];
  if (!allowed.includes(watched as string)) {
    return null;
  }
  return <FieldView control={control} def={def} />;
}

/** Renders every field of one intake section from its config. */
export function IntakeSectionFields({
  control,
  section,
}: {
  readonly control: Ctrl;
  readonly section: IntakeSection;
}): JSX.Element {
  return (
    <div className="space-y-4">
      {INTAKE_SECTION_DEFS[section].map((def) =>
        def.showWhen === undefined ? (
          <FieldView key={def.name} control={control} def={def} />
        ) : (
          <ConditionalField key={def.name} control={control} def={def} />
        ),
      )}
    </div>
  );
}
